/**
 * Writing an interest on a call's page ("seeking a consortium / partners"), the way a post is written: a full-width
 * markdown text box with the post composer's formatting buttons and preview, @mentions (connections are notified)
 * and the kind of interest. Enter makes a new line; Ctrl/⌘ + Enter or the button posts it. With INTEREST it edits
 * that interest in place instead (its mentions stay links).
 */
import React, { useRef, useState } from 'react';
import { Avatar, Box, Button, Chip, CircularProgress, IconButton, Paper, TextField, Typography } from '@mui/material';
import { Send as SendIcon } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { axiosService } from 'shared/helpers/axios';
import LabMarkdown from 'components/collab/lab/LabMarkdown';
import MarkdownToolbar, { useMarkdownEditing } from 'components/common/MarkdownToolbar';
import { mentionsIn, plainMentions, useMentions } from 'components/social/feed/mentions';
import { POST_MAX_LENGTH, lengthHint, postMarkdown } from 'shared/helpers/postText';

const KINDS = [[0, 'Seeking a consortium'], [1, 'Seeking partners']];

/** ONSUBMIT(body, typeOfInterest): posts (or saves) it, rejecting to keep the draft; ONCANCEL leaves editing. */
export default function InterestComposer({ currentUser, onSubmit, composerRef, interest = null, onCancel }) {
  const theme = useTheme();
  const [body, setBody] = useState(() => plainMentions(interest?.body || ''));
  const [kind, setKind] = useState(interest?.type_of_interest ?? 0);
  const [preview, setPreview] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const inputRef = useRef(null);
  const mentions = useMentions(body, setBody, inputRef, interest ? mentionsIn(interest.body || '') : []);
  const editing = useMarkdownEditing(body, setBody, inputRef, () => setPreview(false));

  const submit = async () => {
    if (!body.trim() || submitting) return;
    setSubmitting(true);
    try {
      await onSubmit(mentions.apply(body.trim()), kind);
      if (interest) return;           // the edited interest is shown again
      setBody('');
      setPreview(false);
      mentions.reset();
    } catch {
      // the caller shows the error; the draft stays
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Paper ref={composerRef} variant="outlined" sx={{ p: { xs: 1.5, sm: 2 }, my: interest ? 1 : 2, borderRadius: 2, minWidth: 0 }}>
      <Box display="flex" alignItems="center" gap={1.25} mb={1.25} flexWrap="wrap">
        <Avatar src={currentUser?.avatar || undefined} alt={currentUser?.first_name || 'User'}
          sx={{ width: 32, height: 32, bgcolor: theme.palette.primary.avatar, color: 'white' }}>
          {currentUser?.first_name?.[0]}
        </Avatar>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mr: 'auto' }}>{interest ? 'Edit your interest' : 'Share your interest in this call'}</Typography>
        <Box display="flex" gap={0.75} role="radiogroup" aria-label="Type of interest">
          {KINDS.map(([value, label]) => (
            <Chip key={value} size="small" label={label} role="radio" aria-checked={kind === value}
              color={kind === value ? 'primary' : 'default'} variant={kind === value ? 'filled' : 'outlined'}
              onClick={() => setKind(value)} />
          ))}
        </Box>
      </Box>

      <TextField
        fullWidth
        multiline
        minRows={4}
        maxRows={16}
        inputRef={inputRef}
        inputProps={{ maxLength: POST_MAX_LENGTH }}
        helperText={lengthHint(body)}
        placeholder="What are you looking for? Your expertise, the partners or roles you need, what you bring… (@ to mention)"
        value={body}
        {...mentions.field}
        onKeyDown={(e) => {
          mentions.field.onKeyDown(e);
          if (!e.defaultPrevented && e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); submit(); }
        }}
        style={preview ? { display: 'none' } : undefined}
        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
      />
      {mentions.list}

      {preview && (
        <Box sx={{ minHeight: 110, px: 1.75, py: 1.25, borderRadius: '12px', border: '1px solid', borderColor: 'divider',
          wordBreak: 'break-word' }}>
          {body.trim()
            ? <LabMarkdown images hashtags>{postMarkdown(body)}</LabMarkdown>
            : <Typography variant="body2" color="text.secondary">Nothing to preview yet.</Typography>}
        </Box>
      )}

      <MarkdownToolbar editing={editing} preview={preview} onTogglePreview={() => setPreview(!preview)}>
        <Button variant="contained" size="small" disableElevation onClick={submit} disabled={!body.trim() || submitting}
          endIcon={submitting ? <CircularProgress size={14} color="inherit" /> : <SendIcon fontSize="small" />}
          sx={{ textTransform: 'none', borderRadius: 2, order: 1, ml: 0.5 }}>
          {interest ? 'Save' : 'Post interest'}
        </Button>
        {onCancel && (
          <Button size="small" onClick={onCancel} disabled={submitting} sx={{ textTransform: 'none', order: 1 }}>Cancel</Button>
        )}
      </MarkdownToolbar>
    </Paper>
  );
}

/** A reply to an interest (also on the Interests page): plain text with @mentions. */
export function InterestReplyForm({ parentId, onSubmitted, onCancel }) {
  const [body, setBody] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // "@…" in a reply: the same list of people (then consortia, organizations) to mention as in posts
  const inputRef = useRef(null);
  const mentions = useMentions(body, setBody, inputRef);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!body.trim()) return;
    setSubmitting(true);
    try {
      await axiosService.post('/interact/comments/', {
        parent: parentId,
        target_type: 'comment',
        target_id: parentId,
        body: mentions.apply(body.trim()),
        type_of_interest: 0,
      });
      setBody('');
      mentions.reset();
      onSubmitted();
    } catch (err) {
      console.error('Failed to post reply:', err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ mt: 1.5 }}>
    <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1 }}>
      <TextField
        fullWidth
        size="small"
        placeholder="Write a reply… (@ to mention)"
        value={body}
        inputRef={inputRef}
        {...mentions.field}
        multiline
        maxRows={4}
        autoFocus
      />
      {/* <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}> */}
        <IconButton
          type="submit"
          color="primary"
          disabled={!body.trim() || submitting}
          size="large"
        >
          {submitting ? <CircularProgress size={16} /> : <SendIcon fontSize="large" />}
        </IconButton>
        
      {/* </Box> */}
    </Box>
    {mentions.list}
    </Box>
  );
}
